// src/toolkitServices/labelTemplateApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const labelTemplateApi = createApi({
  reducerPath: "labelTemplateApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL, // e.g. http://localhost:4000/api
    credentials: "include",
  }),
  tagTypes: ["LabelTemplate"],
  endpoints: (builder) => ({
    // CREATE: POST /api/label-templates/create
    addLabelTemplate: builder.mutation({
      query: (payload) => ({
        url: "/label-templates/create",
        method: "POST",
        body: payload, // { name, widthMm, heightMm, dpi, branchIds, categoryIds, elements, ... }
      }),
      invalidatesTags: ["LabelTemplate"],
    }),

    // LIST: GET /api/label-templates?clientCode=...&branchIds=...,&categoryIds=...
    getAllLabelTemplates: builder.query({
      // args: { clientCode, branchIds, categoryIds }
      query: ({ clientCode, branchIds, categoryIds, templateType } = {}) => {
        const params = {};

        if (clientCode) params.clientCode = clientCode;
        if (templateType) params.templateType = templateType;
        // optional filters – backend pe querystring parse karna
        if (Array.isArray(branchIds) && branchIds.length > 0) {
          params.branchIds = branchIds.join(","); // e.g. ?branchIds=1,2,3
        }

        if (Array.isArray(categoryIds) && categoryIds.length > 0) {
          params.categoryIds = categoryIds.join(","); // e.g. ?categoryIds=a,b
        }

        return {
          url: "/label-templates",
          params,
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((t) => ({
                type: "LabelTemplate",
                id: t._id,
              })),
              { type: "LabelTemplate", id: "LIST" },
            ]
          : [{ type: "LabelTemplate", id: "LIST" }],
    }),

    // GET ONE: GET /api/label-templates/:id
    getLabelTemplateById: builder.query({
      query: (id) => `/label-templates/${id}`,
      providesTags: (_res, _err, id) => [{ type: "LabelTemplate", id }],
    }),

    // UPDATE: PUT /api/label-templates/:id
    updateLabelTemplate: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/label-templates/${id}`,
        method: "PUT",
        body: patch, // same shape as create: can include branchIds/categoryIds update
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "LabelTemplate", id },
        { type: "LabelTemplate", id: "LIST" },
      ],
    }),

    // DELETE: DELETE /api/label-templates/:id
    deleteLabelTemplate: builder.mutation({
      query: (id) => ({
        url: `/label-templates/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "LabelTemplate", id },
        { type: "LabelTemplate", id: "LIST" },
      ],
    }),

    // SET DEFAULT: PATCH /api/label-templates/:id/set-default
    setDefaultLabelTemplate: builder.mutation({
      query: (id) => ({
        url: `/label-templates/${id}/set-default`,
        method: "PATCH",
      }),
      invalidatesTags: ["LabelTemplate"],
    }),
  }),
});

export const {
  useAddLabelTemplateMutation,
  useGetAllLabelTemplatesQuery,
  useGetLabelTemplateByIdQuery,
  useUpdateLabelTemplateMutation,
  useDeleteLabelTemplateMutation,
  useSetDefaultLabelTemplateMutation,
} = labelTemplateApi;

export default labelTemplateApi;
